import { SearchLg } from "@untitledui/icons";
import { type ReactNode, useRef, useState } from "react";
import {
	Dialog as AriaDialog,
	DialogTrigger as AriaDialogTrigger,
	Popover as AriaPopover,
} from "react-aria-components";
import { Button } from "@/components/base/buttons/button";
import { useAppTranslation } from "@/hooks/use-app-translation";
import { cx } from "@/utils/cx";
import { ICON_CATALOG, resolveIconComponent } from "@/utils/icon-catalog";

interface IconPickerPopoverProps {
	value: string;
	onChange: (iconName: string) => void;
	label?: string;
	isRequired?: boolean;
	triggerTestId?: string;
	searchInputTestId?: string;
	optionTestIdPrefix?: string;
	/**
	 * When provided, restricts the picker grid to these icon names.
	 * Search still operates within the allowed set.
	 * The currently selected icon is always displayable regardless of this list.
	 */
	allowedIcons?: string[];
	/**
	 * Custom popover trigger (must be a React Aria pressable, e.g. `AriaButton`).
	 * When provided, `label` is not rendered and the default round button is replaced.
	 */
	trigger?: ReactNode;
}

export const IconPickerPopover = ({
	value,
	onChange,
	label,
	isRequired,
	triggerTestId,
	searchInputTestId,
	optionTestIdPrefix,
	allowedIcons,
	trigger,
}: IconPickerPopoverProps) => {
	const { t } = useAppTranslation();
	const [search, setSearch] = useState("");
	const searchInputRef = useRef<HTMLInputElement>(null);

	const ResolvedIcon = resolveIconComponent(value);
	// Fall back to the first catalog icon when the stored value is unknown
	const SelectedIcon = ResolvedIcon ?? ICON_CATALOG[0].component;

	const catalog = allowedIcons
		? ICON_CATALOG.filter((e) => allowedIcons.includes(e.name))
		: ICON_CATALOG;

	const filtered = search
		? catalog.filter((e) => e.name.toLowerCase().includes(search.toLowerCase()))
		: catalog;

	return (
		<div className="flex flex-col gap-1.5">
			{label && !trigger && (
				<span className="text-sm font-medium text-secondary">
					{label}
					{isRequired ? (
						<span
							className="ml-0.5 font-medium text-fg-error-primary"
							aria-hidden
						>
							*
						</span>
					) : null}
				</span>
			)}
			<AriaDialogTrigger>
				{trigger ?? (
					<Button
						data-testid={triggerTestId}
						color="secondary"
						className="flex h-10 w-10 items-center justify-center rounded-full hover:bg-blue-50 hover:ring-2 hover:ring-blue-600 "
					>
						<SelectedIcon size={18} />
					</Button>
				)}
				<AriaPopover
					placement="bottom start"
					offset={4}
					className={({ isEntering, isExiting }) =>
						cx(
							"w-90 rounded-lg bg-primary shadow-lg ring-1 ring-secondary_alt outline-hidden",
							isEntering &&
								"duration-150 ease-out animate-in fade-in slide-in-from-top-0.5",
							isExiting &&
								"duration-100 ease-in animate-out fade-out slide-out-to-top-0.5",
						)
					}
				>
					<AriaDialog className="outline-hidden">
						{({ close }) => (
							<div className="flex flex-col">
								<div className="px-3 pt-3 pb-2">
									<div className="flex items-center gap-2 rounded-md border border-gray-200 px-2.5 py-1.5">
										<SearchLg className="text-gray-400" size={16} />
										<input
											data-testid={searchInputTestId}
											ref={searchInputRef}
											type="text"
											className="flex-1 bg-transparent text-sm outline-none placeholder:text-gray-400"
											placeholder={t("admin.iconPicker.inputPlaceholder")}
											value={search}
											onChange={(e) => setSearch(e.target.value)}
											// biome-ignore lint/a11y/noAutofocus: search input should focus on open
											autoFocus
										/>
									</div>
								</div>
								<div className="grid grid-cols-6 gap-2 px-3 pb-3 max-h-56 overflow-y-auto">
									{filtered.map((entry) => {
										const Icon = entry.component;
										const isSelected = entry.name === value;
										return (
											<button
												data-testid={
													optionTestIdPrefix
														? `${optionTestIdPrefix}-${entry.name}`
														: undefined
												}
												key={entry.name}
												type="button"
												title={entry.name}
												className={cx(
													"flex items-center justify-center h-12 w-12 rounded-lg border-2 transition-all hover:bg-gray-50 border-gray-200",
													isSelected
														? "bg-blue-50 text-blue-600 border-blue-600"
														: "text-gray-500 hover:bg-gray-100 hover:text-gray-700",
												)}
												onClick={() => {
													onChange(entry.name);
													setSearch("");
													close();
												}}
											>
												<Icon size={20} />
											</button>
										);
									})}
									{filtered.length === 0 && (
										<p className="col-span-7 py-4 text-center text-sm text-gray-400">
											{t("admin.iconPicker.noIcons")}
										</p>
									)}
								</div>
								<div className="border-t border-gray-100 px-3 py-2">
									<p className="text-xs text-gray-400">
										{filtered.length} {t("admin.iconPicker.iconsCount")}
									</p>
								</div>
							</div>
						)}
					</AriaDialog>
				</AriaPopover>
			</AriaDialogTrigger>
		</div>
	);
};
